import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search, Bell, ExternalLink, User, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PAGE_TITLES = {
    "/admin": "Dashboard",
    "/admin/products": "Event packages",
    "/admin/occasions": "Occasion galleries",
    "/admin/categories": "Event types",
    "/admin/collections": "Experience collections",
    "/admin/attributes": "Package details",
    "/admin/inventory": "Package availability",
    "/admin/orders": "Bookings",
    "/admin/shipments": "Shipments",
    "/admin/returns": "Returns & Refunds",
    "/admin/banners": "Event banners",
    "/admin/reels": "Event films",
    "/admin/coupons": "Coupons",
    "/admin/customers": "Customers",
    "/admin/analytics": "Analytics",
    "/admin/blog": "Event journal",
    "/admin/reviews": "Event testimonials",
    "/admin/support": "Event enquiries",
    "/admin/loyalty": "Loyalty",
    "/admin/campaigns": "Email campaigns",
    "/admin/site-content": "Pages & content",
    "/admin/menus": "Menus",
    "/admin/seo": "SEO",
    "/admin/suppliers": "Suppliers",
    "/admin/purchase-orders": "Purchase orders",
    "/admin/reports/sales": "Sales reports",
    "/admin/reports/inventory": "Inventory reports",
    "/admin/settings": "Settings",
    "/admin/roles": "User roles",
    "/admin/activity": "Activity logs",
};

export default function AdminTopBar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [search, setSearch] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [pending, setPending] = useState(0);

    const title = PAGE_TITLES[location.pathname.replace(/\/edit$/, "").replace(/\/[^/]+$/, m => PAGE_TITLES[location.pathname] ? m : "")] || PAGE_TITLES["/admin/products"];
    const pageTitle = PAGE_TITLES[location.pathname] || (location.pathname.includes("/admin/products") ? "Products" : "Admin");

    useEffect(() => {
        api.get("/admin/overview").then(r => {
            setLowStock(r.data.low_stock || []);
            setPending(r.data.pending_orders || 0);
        }).catch(() => {});
    }, [location.pathname]);

    useEffect(() => {
        if (!search.trim()) { setSuggestions([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/admin/products`);
                const filtered = r.data.filter(p => !p.demo_catalog && !p.legacy_store_content).filter(p =>
                    p.title.toLowerCase().includes(search.toLowerCase()) ||
                    p.slug.toLowerCase().includes(search.toLowerCase())
                ).slice(0, 6);
                setSuggestions(filtered);
            } catch { setSuggestions([]); }
        }, 220);
        return () => clearTimeout(t);
    }, [search]);

    const totalAlerts = lowStock.length + pending;

    return (
        <div className="sticky top-0 z-30 -mx-6 lg:-mx-10 px-6 lg:px-10 py-4 bg-[#FAF7F1]/85 backdrop-blur-xl border-b border-[#E7DED2] mb-6">
            <div className="flex items-center gap-3">
                <div className="hidden sm:block">
                    <p className="font-mono-ui text-[10px] text-muted-foreground">X-Plosiv Studio</p>
                    <h2 className="font-display text-xl leading-none mt-0.5">{pageTitle}</h2>
                </div>
                {/* Search */}
                <div className="flex-1 max-w-md relative ml-0 sm:ml-6">
                    <div className="flex items-center gap-2 rounded-full bg-white border border-[#E7DED2] px-4 h-10">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search event packages…" className="flex-1 bg-transparent outline-none text-sm" data-testid="admin-topbar-search" />
                    </div>
                    {suggestions.length > 0 && (
                        <div className="absolute top-12 left-0 right-0 bg-white rounded-2xl border border-[#E7DED2] shadow-[0_18px_44px_rgba(18,18,20,0.14)] p-2 z-40">
                            {suggestions.map(s => (
                                <button key={s.id} onClick={() => { navigate(`/admin/products/${s.id}/edit`); setSearch(""); setSuggestions([]); }} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-[#FAF7F1] text-left">
                                    <img src={s.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">{s.title}</p>
                                        <p className="text-xs text-muted-foreground">{s.category}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Notifications */}
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="relative h-10 w-10 rounded-full bg-white border border-[#E7DED2] flex items-center justify-center hover:bg-[#FAF7F1]" data-testid="admin-topbar-notifications">
                            <Bell className="h-4 w-4" />
                            {totalAlerts > 0 && <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#C07A5A] text-white text-[10px] font-semibold flex items-center justify-center">{totalAlerts}</span>}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="rounded-2xl w-80">
                        <h4 className="font-mono-ui text-xs mb-3">Notifications</h4>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                            {pending > 0 && (
                                <button onClick={() => navigate("/admin/orders")} className="w-full text-left p-3 rounded-xl bg-[#FAF7F1] hover:bg-[#F4EFE6]">
                                    <p className="text-sm font-medium">{pending} pending orders</p>
                                    <p className="text-xs text-muted-foreground">Awaiting payment confirmation</p>
                                </button>
                            )}
                            {lowStock.slice(0, 5).map(l => (
                                <button key={l.sku} onClick={() => navigate("/admin/inventory")} className="w-full text-left p-3 rounded-xl bg-[#FAF7F1] hover:bg-[#F4EFE6]">
                                    <p className="text-sm font-medium">Low stock — {l.title}</p>
                                    <p className="text-xs text-muted-foreground">{l.color}/{l.size} · {l.stock} left</p>
                                </button>
                            ))}
                            {totalAlerts === 0 && <p className="text-sm text-muted-foreground p-3">All clear — nothing urgent.</p>}
                        </div>
                    </PopoverContent>
                </Popover>
                {/* View site */}
                <a href="/" target="_blank" rel="noreferrer" className="hidden sm:flex h-10 px-4 rounded-full bg-white border border-[#E7DED2] items-center gap-2 text-xs font-mono-ui hover:bg-[#FAF7F1]" data-testid="admin-topbar-view-site">
                    <ExternalLink className="h-3.5 w-3.5" /> View site
                </a>
                {/* User */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button className="h-10 w-10 rounded-full bg-[#121214] text-white flex items-center justify-center" data-testid="admin-topbar-user">
                            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full rounded-full object-cover" /> : <User className="h-4 w-4" />}
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-2xl border-[#E7DED2] mt-2 w-56">
                        <div className="px-3 py-2">
                            <p className="text-sm font-medium">{user?.name}</p>
                            <p className="text-xs text-muted-foreground">{user?.email}</p>
                        </div>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => navigate("/account")}>My account</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate("/")}>View site</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => { logout(); navigate("/login"); }}>
                            <LogOut className="h-4 w-4 mr-2" /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
